import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@utriper/core-ui';
import { PriceGroupTabService } from 'core/api/services/priceGroupTab';
import { PriceStatus } from '@utriper/core';

const PriceGroupTabContext = React.createContext(null);

export const PriceGroupTabProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loadingPriceGroups, setLoadingPriceGroups] = useState(false);
    const [priceGroupTabs, setPriceGroupTabs] = useState(null);
    const [totalPriceGroupTabs, setTotalPriceGroupTabs] = useState(0);
    const [allPriceGroupTabCount, setAllPriceGroupTabCount] = useState(0);
    const [priceGroupTab, setPriceGroupTab] = useState(null);
    const [priceGroups, setPriceGroups] = useState([]);
    const [totalPriceGroups, setTotalPriceGroups] = useState(0);
    const [allPriceGroupCount, setAllPriceGroupCount] = useState(0);
    // Filters
    const [pgTabNameFilter, setPgTabNameFilter] = useState('');
    const [pgTabStatusFilter, setPgTabStatusFilter] = useState([]);
    const [pagePgTab, setPagePgTab] = useState(1);
    const nameFilterDebounce = useDebounce(pgTabNameFilter, 300);
    const MAX_PAGE = 12;
    // Price Group Filters
    const [pgNameFilter, setPgNameFilter] = useState('');
    const [pgStatusFilter, setPgStatusFilter] = useState([]);
    const [pgTypeFilter, setPgTypeFilter] = useState([]);
    const [pagePg, setPagePg] = useState(1);
    const pgNameFilterDebounce = useDebounce(pgNameFilter, 300);
    // Others
    const [openForm, setOpenForm] = useState(false);
    const [openUpdateForm, setOpenUpdateForm] = useState(false);
    const { t } = useTranslation();
    const translate = (value) => t(`price_group_tab.form.${value}`);

    // Price Group Tab Utils
    const resetPage = () => {
        if (pagePgTab !== 1) {
            setPagePgTab(1);
        }
    };

    const onChangeSearch = (event) => {
        setPgTabNameFilter(event.target.value);
        resetPage();
    };

    const onChangeStatus = (newValues) => {
        setPgTabStatusFilter([...newValues]);
        resetPage();
    };

    const handlePage = (event, newPage) => {
        setPagePgTab(newPage);
    };

    const clearFilters = (event) => {
        event?.preventDefault();

        setPgTabStatusFilter([]);
        setPgTabNameFilter('');
        resetPage();
    };

    const handleOpenForm = (newStatus = null) => {
        if (newStatus !== null) {
            setOpenForm(newStatus);
            return;
        }
        setOpenForm(!openForm);
    };

    const handleOpenUpdateForm = (newStatus = null) => {
        if (newStatus !== null) {
            setOpenUpdateForm(newStatus);
            return;
        }
        setOpenUpdateForm(!openUpdateForm);
    };

    // Price Group By Id Utils
    const resetPriceGroupTab = (priceGroupTabId) => {
        if (Number.isInteger(priceGroupTab?.id) && priceGroupTab.id !== priceGroupTabId) {
            setPriceGroupTab(null);
        }
    };

    const resetPriceGroups = (priceGroupTabId) => {
        if (Number.isInteger(priceGroupTab?.id) && priceGroupTab.id !== priceGroupTabId) {
            setPgNameFilter('');
            setPgStatusFilter([]);
            setPgTypeFilter([]);
            setPagePg(1);
            setPriceGroups([]);
            setTotalPriceGroups(0);
            setAllPriceGroupCount(0);
        }
    };

    const resetPriceGroupPages = () => {
        if (pagePg !== 1) {
            setPagePg(1);
        }
    };

    const onChangeSearchPriceGroup = (event) => {
        setPgNameFilter(event?.target?.value);
        resetPriceGroupPages();
    };

    const onChangeStatusPriceGroup = (newValues) => {
        setPgStatusFilter([...newValues]);
        resetPriceGroupPages();
    };

    const onChangeTypePriceGroup = (newValues) => {
        setPgTypeFilter([...newValues]);
        resetPriceGroupPages();
    };

    const handlePagePriceGroup = (event, newPage) => {
        setPagePg(newPage);
    };

    const clearFiltersPriceGroup = (event) => {
        event?.preventDefault();

        setPriceGroups([]);
        setTotalPriceGroups(0);
        resetPriceGroupPages();
    };

    // Other utils
    // TODO: Move to core library
    const getStatusColor = (status) => {
        switch (status) {
            case PriceStatus.IDS.draft:
                return '#9EC4D6';
            case PriceStatus.IDS.live:
                return '#50C878';
            case PriceStatus.IDS.archived:
                return '#131313';
            default:
                return '#131313';
        }
    };

    // Fetch Functions
    const getPriceGroupTabs = async () => {
        try {
            setLoading(true);
            const options = { name: nameFilterDebounce, statusIds: pgTabStatusFilter, pager: { page: pagePgTab, pageSize: MAX_PAGE } };
            const { priceGroupTabs = [], total = 0, allPriceGroupTabCount = 0 } = await PriceGroupTabService.getList(options);

            if (!Array.isArray(priceGroupTabs) || !Number.isInteger(total)) {
                throw new Error(translate('errors.get'));
            }

            setPriceGroupTabs(priceGroupTabs);
            setTotalPriceGroupTabs(total);
            setAllPriceGroupTabCount(allPriceGroupTabCount);
            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const createPriceGroupTab = async (options) => {
        try {
            setSubmitting(true);
            const result = await PriceGroupTabService.create(options);

            if (!result) {
                toast.error(translate('errors.message'), {
                    theme: 'colored',
                    position: 'top-right',
                });
                return false;
            }

            toast.success(translate('success_message'), {
                theme: 'colored',
                position: 'top-right',
            });
            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setSubmitting(false);
            setOpenForm(false);
            getPriceGroupTabs();
        }
    };

    const getPriceGroupTabById = async (priceGroupTabId) => {
        try {
            setLoading(true);
            resetPriceGroupTab(priceGroupTabId);

            const { priceGroupTab } = await PriceGroupTabService.getById(priceGroupTabId);

            if (!priceGroupTab?.id) {
                throw new Error(translate('errors.get_by_id'));
            }

            setPriceGroupTab(priceGroupTab);
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const getPriceGroups = async (priceGroupTabId) => {
        try {
            setLoadingPriceGroups(true);
            resetPriceGroups(priceGroupTabId);

            const options = { name: pgNameFilterDebounce, statusIds: pgStatusFilter, typeIds: pgTypeFilter, pager: { page: pagePg, pageSize: MAX_PAGE } };
            const { priceGroups, total, allPriceGroupCount } = await PriceGroupTabService.getPriceGroups(priceGroupTabId, options);

            if (!Array.isArray(priceGroups) || !Number.isInteger(total)) {
                throw new Error(translate('errors.get_prices'));
            }

            setPriceGroups(priceGroups);
            setTotalPriceGroups(total);
            setAllPriceGroupCount(allPriceGroupCount);
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingPriceGroups(false);
        }
    };

    const updatePriceGroupTab = async (priceGroupTabId, options) => {
        try {
            setSubmitting(true);
            const result = await PriceGroupTabService.update(priceGroupTabId, options);

            if (!result) {
                throw new Error(translate('errors.update'));
            }

            toast.success(translate('update_message'), {
                theme: 'colored',
                position: 'top-right',
            });
            return true;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });
            return false;
        } finally {
            setSubmitting(false);
            setOpenUpdateForm(false);
            getPriceGroupTabById(priceGroupTabId);
        }
    };

    return (
        <PriceGroupTabContext.Provider
            value={{
                getPriceGroupTabs,
                createPriceGroupTab,
                getPriceGroupTabById,
                getPriceGroups,
                updatePriceGroupTab,
                // Price Group Tab Utils
                onChangeSearch,
                onChangeStatus,
                handlePage,
                clearFilters,
                handleOpenForm,
                handleOpenUpdateForm,
                // Price Group Tab By Id Utils
                onChangeSearchPriceGroup,
                onChangeStatusPriceGroup,
                onChangeTypePriceGroup,
                handlePagePriceGroup,
                clearFiltersPriceGroup,
                // Other Utils
                getStatusColor,
                // Price Group Tab
                priceGroupTabs,
                totalPriceGroupTabs,
                allPriceGroupTabCount,
                // Filters
                nameFilterDebounce,
                pgTabNameFilter,
                pgTabStatusFilter,
                pagePgTab,
                // Price Group Tab By ID
                priceGroupTab,
                priceGroups,
                totalPriceGroups,
                allPriceGroupCount,
                // Filters
                pgNameFilter,
                pgStatusFilter,
                pgTypeFilter,
                pagePg,
                pgNameFilterDebounce,
                // Others
                loading,
                submitting,
                loadingPriceGroups,
                MAX_PAGE,
                openForm,
                openUpdateForm,
            }}
        >
            {children}
        </PriceGroupTabContext.Provider>
    );
};

PriceGroupTabProvider.displayName = 'PriceGroupTabProvider';

export const usePriceGroupTab = () => useContext(PriceGroupTabContext);
